import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { preferencesApi } from '../api/preferences';
import { preferencesStore } from '../lib/preferencesStore';
import { useAuth } from './AuthContext';

type PreferencesContextType = {
  ready: boolean;
  get: <T>(key: string) => T | null;
  set: (key: string, value: unknown) => void;
};

const PreferencesContext = createContext<PreferencesContextType>({
  ready: false,
  get: () => null,
  set: () => undefined,
});

/**
 * Loads the user's preferences once after login and copies them into
 * sessionStorage before the pages mount, so filters come back exactly as
 * they were left, on any machine.
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) {
      setReady(false);
      return;
    }
    preferencesApi
      .get()
      .then((data) => {
        if (!cancelled) preferencesStore.hydrate(data);
      })
      .catch(() => {
        if (!cancelled) preferencesStore.hydrate({});
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const get = useCallback(<T,>(key: string) => preferencesStore.get<T>(key), []);
  const set = useCallback((key: string, value: unknown) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
    preferencesStore.save(key, value);
  }, []);

  return (
    <PreferencesContext.Provider value={{ ready, get, set }}>{children}</PreferencesContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePreferences() {
  return useContext(PreferencesContext);
}
